// authorLocation — where the Author is as they send a message (principle 22):
// the page open in the Studio, the block the caret is in, the selected text,
// and what the composer's scope chip says the message is about (the open
// page, or the whole course or project). It rides every message the Author
// sends from the Studio, so the assistant reads "here", "this page" or "the
// course" against it. The server resolves every id; this only reports.
//
// Pure. The caret is view state the editor reports as it moves (a fact of
// the page, not of the project; never stored), and counts only on the page
// it was reported on. The selection is cut to the wire's ceiling here, so a
// long selection never makes a send fail.

export type { AuthorCaret, AuthorLocation };
export { authorLocationOf, SELECTION_MAX };

import * as Core from "@bilda/core";

type AuthorScope = "open_page" | "whole_project";

// The block the caret is in on a page, with the selected text there.
type AuthorCaret = {
    readonly pageId: string;
    readonly blockId: string;
    readonly selection?: string;
};

// The wire shape of `sendTurn`'s and `sendWaitingMessage`'s `location`.
type AuthorLocation = {
    readonly pageId: string;
    readonly scope: AuthorScope;
    readonly blockId?: string;
    readonly selection?: string;
};

// The contract's ceiling on the selected text.
const SELECTION_MAX = Core.Limits.LONG_TEXT_MAX;

// Blank: nothing selected. Longer than the ceiling: its start.
const clipSelection = (text: string): string | undefined =>
    text.trim() === "" ? undefined : text.slice(0, SELECTION_MAX);

// Undefined when no page is open (the classic canvas, a page about the
// project).
const authorLocationOf = (
    page: string | undefined,
    scope: AuthorScope,
    caret: AuthorCaret | undefined,
): AuthorLocation | undefined => {
    if (page === undefined) return undefined;
    const here = caret?.pageId === page ? caret : undefined;
    const selection =
        here?.selection === undefined
            ? undefined
            : clipSelection(here.selection);
    return {
        pageId: page,
        scope,
        ...(here === undefined ? {} : { blockId: here.blockId }),
        ...(selection === undefined ? {} : { selection }),
    };
};
